import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, IconButton, Chip } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import MapIcon from '@mui/icons-material/Map';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import GavelIcon from '@mui/icons-material/Gavel';
import BusinessIcon from '@mui/icons-material/Business';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ArticleIcon from '@mui/icons-material/Article';
import FolderIcon from '@mui/icons-material/Folder';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import BalanceIcon from '@mui/icons-material/Balance';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import DescriptionIcon from '@mui/icons-material/Description';
import ForumIcon from '@mui/icons-material/Forum';

const dataTypes = [
  {
    id: 'parliamentarians',
    title: 'Parliamentarians',
    description: 'Search current and former Members of Parliament, Senators, and their biographical information.',
    icon: PersonIcon,
    color: '#1a73e8',
    gradient: 'linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)',
    path: '/parliamentarians',
    stats: '1,877 members'
  },
  {
    id: 'hansard',
    title: 'Hansard',
    description: 'Parliamentary debates from 1901 to present. Search 500,000+ speeches across both chambers.',
    icon: RecordVoiceOverIcon,
    color: '#2e7d32',
    gradient: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
    path: '/hansard',
    stats: '507,000+ speeches'
  },
  {
    id: 'committees',
    title: 'Committees',
    description: 'Parliamentary committees including standing, select, and joint committees with membership details.',
    icon: GroupsIcon,
    color: '#7b1fa2',
    gradient: 'linear-gradient(135deg, #7b1fa2 0%, #4a148c 100%)',
    path: '/committees',
    stats: '465 committees'
  },
  {
    id: 'electorates',
    title: 'Electorates',
    description: 'Electoral divisions with historical boundary maps, demographics, and representative history.',
    icon: MapIcon,
    color: '#00838f',
    gradient: 'linear-gradient(135deg, #00838f 0%, #006064 100%)',
    path: '/electorates',
    stats: '219 electorates'
  },
  {
    id: 'bills',
    title: 'Bills',
    description: 'Legislation before Parliament including bill text, explanatory memoranda, and voting records.',
    icon: ArticleIcon,
    color: '#c62828',
    gradient: 'linear-gradient(135deg, #c62828 0%, #b71c1c 100%)',
    path: '/bills',
    stats: '2,200+ bills'
  },
  {
    id: 'qon',
    title: 'Questions on Notice',
    description: 'Written questions submitted by Members and Senators with ministerial responses.',
    icon: QuestionAnswerIcon,
    color: '#ef6c00',
    gradient: 'linear-gradient(135deg, #ef6c00 0%, #e65100 100%)',
    path: '/qon',
    stats: '1,975 questions'
  },
  {
    id: 'standing-orders-hor',
    title: 'HoR Standing Orders',
    description: 'Rules and procedures governing the House of Representatives.',
    icon: GavelIcon,
    color: '#5c6bc0',
    gradient: 'linear-gradient(135deg, #5c6bc0 0%, #3949ab 100%)',
    path: '/standingorders',
    stats: '247 orders'
  },
  {
    id: 'standing-orders-senate',
    title: 'Senate Standing Orders',
    description: 'Rules and procedures governing the Senate chamber.',
    icon: GavelIcon,
    color: '#c62828',
    gradient: 'linear-gradient(135deg, #c62828 0%, #ad1457 100%)',
    path: '/senate-standing-orders',
    stats: '212 orders'
  },
  {
    id: 'ministries',
    title: 'Ministries',
    description: 'Government ministries, portfolios, and ministerial appointments throughout history.',
    icon: BusinessIcon,
    color: '#00695c',
    gradient: 'linear-gradient(135deg, #00695c 0%, #004d40 100%)',
    path: '/ministries',
    stats: '75 ministries'
  },
  {
    id: 'constitution',
    title: 'Constitution',
    description: 'The Australian Constitution with section-by-section search and annotations.',
    icon: BalanceIcon,
    color: '#6d4c41',
    gradient: 'linear-gradient(135deg, #6d4c41 0%, #4e342e 100%)',
    path: '/constitution',
    stats: '145 sections'
  },
  {
    id: 'tabled-documents',
    title: 'Tabled Documents',
    description: 'Documents formally presented to Parliament including reports, returns, and papers.',
    icon: FolderIcon,
    color: '#546e7a',
    gradient: 'linear-gradient(135deg, #546e7a 0%, #37474f 100%)',
    path: '/tabled-documents',
    stats: '34,000+ documents'
  },
  {
    id: 'divisions',
    title: 'Divisions',
    description: 'Recorded votes in both chambers showing how each member voted on legislation.',
    icon: HowToVoteIcon,
    color: '#d32f2f',
    gradient: 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)',
    path: '/divisions',
    stats: '6,500+ votes'
  },
  {
    id: 'house-practice',
    title: 'House Practice',
    description: 'The authoritative guide to House of Representatives practice and procedure.',
    icon: MenuBookIcon,
    color: '#1565c0',
    gradient: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
    path: '/house-practice',
    stats: '21 chapters'
  },
  {
    id: 'calendar',
    title: 'Parliament Calendar',
    description: 'Sitting dates, events, and scheduled business for both chambers.',
    icon: CalendarMonthIcon,
    color: '#7cb342',
    gradient: 'linear-gradient(135deg, #7cb342 0%, #558b2f 100%)',
    path: '/calendar',
    stats: '4,400+ events'
  },
  {
    id: 'senate-journals',
    title: 'Senate Journals',
    description: 'Official record of Senate proceedings, motions, and decisions.',
    icon: DescriptionIcon,
    color: '#c62828',
    gradient: 'linear-gradient(135deg, #c62828 0%, #b71c1c 100%)',
    path: '/senate-journals',
    stats: '100 journals'
  },
  {
    id: 'votes-proceedings',
    title: 'Votes & Proceedings',
    description: 'Official record of House of Representatives business and proceedings.',
    icon: ForumIcon,
    color: '#1565c0',
    gradient: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
    path: '/votes-proceedings',
    stats: '100 records'
  }
];

export default function DataTypeCarousel() {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const containerRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const totalCards = dataTypes.length;
  const visibleCards = 5; // Number of cards visible at once

  const goToNext = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % totalCards);
    setTimeout(() => setIsAnimating(false), 400);
  };

  const goToPrev = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev - 1 + totalCards) % totalCards);
    setTimeout(() => setIsAnimating(false), 400);
  };

  const goToIndex = (index) => {
    if (isAnimating || index === currentIndex) return;
    setIsAnimating(true);
    setCurrentIndex(index);
    setTimeout(() => setIsAnimating(false), 400);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'ArrowRight') goToNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isAnimating]);

  // Touch/swipe support
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) goToNext();
      else goToPrev();
    }
  };

  // Mouse wheel support
  const handleWheel = (e) => {
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      e.preventDefault();
      if (e.deltaX > 30) goToNext();
      else if (e.deltaX < -30) goToPrev();
    }
  };

  const getCardStyle = (index) => {
    // Calculate position relative to current card
    let offset = index - currentIndex;

    // Handle wrapping
    if (offset > totalCards / 2) offset -= totalCards;
    if (offset < -totalCards / 2) offset += totalCards;

    const isCurrent = offset === 0;
    const isVisible = Math.abs(offset) <= Math.floor(visibleCards / 2);

    // 3D transforms
    const translateX = offset * 180; // Horizontal spacing
    const translateZ = isCurrent ? 100 : -Math.abs(offset) * 80; // Depth
    const rotateY = offset * -25; // Rotation around Y axis
    const scale = isCurrent ? 1 : Math.max(0.7, 1 - Math.abs(offset) * 0.15);
    const opacity = isVisible ? Math.max(0.3, 1 - Math.abs(offset) * 0.3) : 0;
    const zIndex = 100 - Math.abs(offset);

    return {
      position: 'absolute',
      left: '50%',
      transform: `
        translateX(-50%)
        translateX(${translateX}px)
        translateZ(${translateZ}px)
        rotateY(${rotateY}deg)
        scale(${scale})
      `,
      opacity,
      zIndex,
      transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      pointerEvents: isCurrent ? 'auto' : 'none',
      cursor: isCurrent ? 'pointer' : 'default'
    };
  };

  const currentCard = dataTypes[currentIndex];
  const IconComponent = currentCard.icon;

  return (
    <Box sx={{ width: '100%', py: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Explore Parliamentary Data
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Browse {totalCards} different data types from the Australian Parliament
        </Typography>
      </Box>

      {/* 3D Carousel Container */}
      <Box
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onWheel={handleWheel}
        sx={{
          position: 'relative',
          height: 400,
          perspective: '1200px',
          perspectiveOrigin: '50% 50%',
          overflow: 'visible',
          mb: 4
        }}
      >
        {/* Navigation Arrows */}
        <IconButton
          onClick={goToPrev}
          sx={{
            position: 'absolute',
            left: { xs: 10, md: 50 },
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 200,
            bgcolor: 'background.paper',
            boxShadow: 3,
            '&:hover': { bgcolor: 'background.paper', transform: 'translateY(-50%) scale(1.1)' }
          }}
        >
          <ArrowBackIosNewIcon />
        </IconButton>

        <IconButton
          onClick={goToNext}
          sx={{
            position: 'absolute',
            right: { xs: 10, md: 50 },
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 200,
            bgcolor: 'background.paper',
            boxShadow: 3,
            '&:hover': { bgcolor: 'background.paper', transform: 'translateY(-50%) scale(1.1)' }
          }}
        >
          <ArrowForwardIosIcon />
        </IconButton>

        {/* Cards */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            transformStyle: 'preserve-3d'
          }}
        >
          {dataTypes.map((dataType, index) => {
            const Icon = dataType.icon;
            const isCurrent = index === currentIndex;

            return (
              <Box
                key={dataType.id}
                onClick={() => isCurrent && navigate(dataType.path)}
                sx={{
                  ...getCardStyle(index),
                  width: 300,
                  height: 360,
                  borderRadius: 4,
                  background: dataType.gradient,
                  boxShadow: isCurrent
                    ? '0 25px 50px -12px rgba(0, 0, 0, 0.4)'
                    : '0 10px 30px -5px rgba(0, 0, 0, 0.3)',
                  overflow: 'hidden',
                  '&:hover': isCurrent ? {
                    transform: `
                      translateX(-50%)
                      translateX(0px)
                      translateZ(120px)
                      rotateY(0deg)
                      scale(1.02)
                    `,
                    boxShadow: '0 30px 60px -15px rgba(0, 0, 0, 0.5)'
                  } : {}
                }}
              >
                {/* Card Content */}
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    p: 3,
                    color: 'white'
                  }}
                >
                  {/* Icon */}
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: 'rgba(255,255,255,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2
                    }}
                  >
                    <Icon sx={{ fontSize: 48, color: 'white' }} />
                  </Box>

                  {/* Title */}
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    {dataType.title}
                  </Typography>

                  {/* Stats Chip */}
                  <Chip
                    label={dataType.stats}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.25)',
                      color: 'white',
                      fontWeight: 600,
                      mb: 2,
                      width: 'fit-content'
                    }}
                  />

                  {/* Description */}
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.9,
                      lineHeight: 1.6,
                      flexGrow: 1
                    }}
                  >
                    {dataType.description}
                  </Typography>

                  {/* Click prompt */}
                  {isCurrent && (
                    <Typography
                      variant="caption"
                      sx={{
                        opacity: 0.7,
                        mt: 2,
                        textAlign: 'center'
                      }}
                    >
                      Click to explore
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Dot Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
        {dataTypes.map((dataType, index) => (
          <Box
            key={dataType.id}
            onClick={() => goToIndex(index)}
            sx={{
              width: index === currentIndex ? 24 : 10,
              height: 10,
              borderRadius: 5,
              bgcolor: index === currentIndex ? dataType.color : 'grey.300',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: index === currentIndex ? dataType.color : 'grey.400',
                transform: 'scale(1.2)'
              }
            }}
          />
        ))}
      </Box>

      {/* Current Card Info (below carousel) */}
      <Box sx={{ textAlign: 'center', mt: 3 }}>
        <Typography variant="h6" sx={{ color: currentCard.color, fontWeight: 600 }}>
          {currentIndex + 1} / {totalCards}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Use arrow keys, swipe, or click arrows to navigate
        </Typography>
      </Box>
    </Box>
  );
}
